import {
    FileDown,
    Search,
    ArrowLeft,
    X,
    Loader2,
} from 'lucide-react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { attendanceApi, traineeRegistrationApi } from '../api';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useAuth } from '../hooks/useAuth';
import { TRAINER_TAG_BADGE_STYLES } from "../constants/trainers";
import ModalPortal from './ModalPortal';

// --- API Response Type Definitions ---
// These interfaces reflect the structure of your backend API responses.
interface Program {
    program_id: number;
    description?: string
    program_code: string;
    name: string;
}

interface User {
    full_name: string;
    user_id?: string;
}

interface Trainer {
    user: User;
}

interface TraineeFromBatch {
    batch_id: number;
    trainee_id: number;
    attendance_percentage: string;
    trainee: {
        user: User;
    };
}

export interface BatchApiResponse {
    batch_id: number;
    batch_code: string;
    program: Program;
    trainer: Trainer;
    trainerTag?: string;
    trainerName?: string;
    mobilisation_id?: number;
    launchpadSessionsRecorded?: number;
    launchpadAvgPct?: number | null;
    batch_trainees: TraineeFromBatch[];
    start_date: string;
    end_date: string;
    status: string;
    batch_proposal_status: string;
    attendancePercentage: number;
}

export interface AttendanceRecordApiResponse {
    batch_id: number;
    trainee_id: number;
    session_date: string;
    present: boolean;
    session_type?: SessionType | null;
    session_hours?: number | null;
    remarks?: string | null;
    trainee: {
        user: User;
    };
}

// --- Flattened Types for Component State ---
// These types make it easier to work with the data inside the components.
interface Batch {
    batchId: string;
    program: string;
    trainer: string;
    trainerTag: string;
    launchpadSessionsRecorded?: number;
    launchpadAvgPct?: number | null;
    trainees: number;
    attendancePercentage: number;
    status: string;
    fullBatchData: BatchApiResponse;
}

interface Trainee {
    id: number;
    name: string;
}

interface TraineeWithAttendance extends Trainee {
    attendance: {
        [date: string]: {
            status: 'P' | 'A' | 'N/A';
            remarks?: string;
            type?: SessionType | '';
        };
    };
}


export interface AttendancePayloadItem {
    trainee_id: number;
    batch_id: number;
    session_date: string;
    present: boolean;
    remarks?: string;
}


type SessionType = 'LAUNCHPAD' | 'BASECAMP' | 'DOMAIN' | 'OJT';

const typeLabel = (t?: SessionType | '' | null) =>
    t === 'LAUNCHPAD' ? 'Launchpad' :
        t === 'BASECAMP' ? 'Basecamp' :
            t === 'DOMAIN' ? 'Domain' :
                t === 'OJT' ? 'OJT' : 'N/A';

const typeBadge = (t?: SessionType | '' | null) =>
    t === 'LAUNCHPAD' ? 'bg-blue-100 text-blue-800' :
        t === 'BASECAMP' ? 'bg-green-100 text-green-800' :
            t === 'DOMAIN' ? 'bg-amber-100 text-amber-800' :
                t === 'OJT' ? 'bg-rose-100 text-rose-800' :
                    'bg-gray-100 text-gray-600';

const prettyMD = (s: string) => new Date(`${s}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const dotClass = (t?: SessionType | '' | null) =>
    t === 'LAUNCHPAD' ? 'bg-blue-500' :
        t === 'BASECAMP' ? 'bg-green-500' :
            t === 'DOMAIN' ? 'bg-amber-500' :
                t === 'OJT' ? 'bg-rose-500' : 'bg-gray-400';

const SESSION_OPTIONS: SessionType[] = ['LAUNCHPAD', 'BASECAMP', 'DOMAIN', 'OJT'];

// helpers (local date, no UTC shift)
const fmtLocal = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parseLocal = (s?: string) => s ? new Date(+s.slice(0, 4), +s.slice(5, 7) - 1, +s.slice(8, 10)) : null;
type TraineeSession = {
    date: string;
    present: boolean;
    type?: SessionType | null;
    hours?: number | null;
    remarks?: string | null;
};


const RemarksModal = ({ trainee, date, onClose }: { trainee: TraineeWithAttendance, date: string, onClose: () => void }) => {
    const dailyRemark = trainee?.attendance[date]?.remarks;

    return (
        <ModalPortal>
            <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-600 bg-opacity-75 transition-opacity flex justify-center items-center">
                <div className="relative bg-white rounded-2xl shadow-xl p-6 max-w-lg w-full m-4">
                <div className="flex justify-between items-center pb-3 border-b">
                    <h3 className="text-xl font-bold text-gray-900">Remarks for {trainee?.name}</h3>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-200">
                        <X size={20} />
                    </button>
                </div>
                <div className="mt-4 max-h-96 overflow-y-auto">
                    <p className="text-sm font-semibold text-gray-500 mb-2">Date: {new Date(date).toLocaleDateString()}</p>
                    {dailyRemark ? (
                        <div className="bg-gray-50 p-3 rounded-md">
                            <p className="text-sm text-gray-700">{dailyRemark}</p>
                        </div>
                    ) : (
                        <p className="text-gray-500 text-sm">No remarks for this date.</p>
                    )}
                </div>
                </div>
            </div>
        </ModalPortal>
    );
};

// Component to display the detailed attendance for a single batch
const BatchDetails = ({ batch, onBack, onEditAttendance }: { batch: Batch, onBack: () => void, onEditAttendance: (batch: Batch) => void }) => {
    ;
    const [traineeData, setTraineeData] = useState<TraineeWithAttendance[]>([]);
    const [allDates, setAllDates] = useState<string[]>([]);
    const [dateToType, setDateToType] = useState<Record<string, string>>({});
    const [hoursByDate, setHoursByDate] = useState<Record<string, number | null>>({});
    const [launchpadDates, setLaunchpadDates] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedTraineeForRemarks, setSelectedTraineeForRemarks] = useState<TraineeWithAttendance | null>(null);
    const [selectedDateForRemarks, setSelectedDateForRemarks] = useState<string | null>(null);
    const [searchTrainee, setSearchTrainee] = useState('');
    const filteredTrainees = useMemo(() => {
        const s = searchTrainee.trim().toLowerCase();
        if (!s) return traineeData;
        return traineeData.filter(t => t.name.toLowerCase().includes(s));
    }, [traineeData, searchTrainee]);

    const [summary, setSummary] = useState<{
        recorded_sessions_total: number;
        recorded_sessions_by_type: Record<SessionType, number>;
        batch_avg_pct_conducted: number;
        trainee_avg_pct_conducted: number;
        session_hours_by_date: Record<string, number | null>;
        daily: { date: string; pct: number; hours: number | null }[];
        launchpad_sessions_total: number;
        launchpad_daily: { date: string; pct: number; present: number; roster: number; hours: number | null }[];
    } | null>(null);

    const conductedDatesSet = useMemo(() => {
        const dates = summary?.daily?.map(d => d.date) ?? [];
        return new Set(dates);
    }, [summary]);

    const launchpadDatesSet = useMemo(() => {
        if (summary?.launchpad_daily?.length) {
            return new Set(summary.launchpad_daily.map(d => d.date));
        }
        return new Set(launchpadDates);
    }, [summary, launchpadDates]);

    const averageHours = useMemo(() => {
        if (!summary?.daily?.length) return null;
        const values = summary.daily
            .map(d => d.hours)
            .filter((h): h is number => h !== null && h !== undefined);
        if (!values.length) return null;
        const total = values.reduce((sum, value) => sum + value, 0);
        return Math.round((total / values.length) * 100) / 100;
    }, [summary]);

    const statsById = useMemo(() => {
        const map = new Map<number, { present: number; absent: number; unmarked: number; pct: number; denom: number }>();
        const denom = conductedDatesSet.size;
        traineeData.forEach(t => {
            let present = 0, absent = 0, unmarked = 0;
            conductedDatesSet.forEach(d => {
                const a = t.attendance[d];
                if (!a) unmarked++;
                else if (a.status === 'P') present++;
                else absent++;
            });
            const pct = denom ? Math.round((present / denom) * 100) : 0;
            map.set(t.id, { present, absent, unmarked, pct, denom });
        });
        return map;
    }, [traineeData, conductedDatesSet]);

    const launchpadStatsById = useMemo(() => {
        const map = new Map<number, { present: number; total: number; pct: number }>();
        const total = launchpadDatesSet.size;
        traineeData.forEach((t) => {
            let present = 0;
            launchpadDatesSet.forEach((d) => {
                const att = t.attendance[d];
                if (att?.status === 'P') {
                    present += 1;
                }
            });
            const pct = total ? Math.round((present / total) * 100) : 0;
            map.set(t.id, { present, total, pct });
        });
        return map;
    }, [traineeData, launchpadDatesSet]);

    const launchpadAveragePct = useMemo(() => {
        if (!launchpadDatesSet.size) return null;
        if (!traineeData.length) return null;
        let totalPct = 0;
        let counted = 0;
        traineeData.forEach((t) => {
            const stats = launchpadStatsById.get(t.id);
            if (stats && stats.total) {
                totalPct += stats.pct;
                counted += 1;
            }
        });
        if (!counted) return null;
        return Math.round((totalPct / counted) * 100) / 100;
    }, [launchpadStatsById, traineeData, launchpadDatesSet]);


    useEffect(() => {
        const load = async () => {
            setIsLoading(true);
            setError(null);
            try {

                const sessRes = await attendanceApi.getBatchSessions(String(batch.fullBatchData.batch_id));
                const recRes = await attendanceApi.getBatchAttendance(String(batch.fullBatchData.batch_id));
                const sumRes = await attendanceApi.getBatchSummary(String(batch.fullBatchData.batch_id));

                if (sessRes.success && recRes.data && sumRes.success) {
                    const toHourValue = (value: any): number | null => {
                        if (value === null || value === undefined || value === '') {
                            return null;
                        }
                        const num = Number(value);
                        return Number.isFinite(num) ? Math.round(num * 100) / 100 : null;
                    };

                    const normalizeHoursMap = (input: Record<string, any> | undefined): Record<string, number | null> => {
                        const result: Record<string, number | null> = {};
                        if (input && typeof input === 'object') {
                            Object.entries(input).forEach(([key, value]) => {
                                result[key] = toHourValue(value);
                            });
                        }
                        return result;
                    };

                    const sessions: string[] = sessRes.data?.sessions_all ?? []; // ['YYYY-MM-DD', ...]
                    setAllDates(sessions); // render only session columns
                    setHoursByDate(normalizeHoursMap(sessRes.data?.session_hours_by_date));
                    const rawLaunchpad = Array.isArray(sessRes.data?.launchpad_sessions) ? sessRes.data.launchpad_sessions : [];
                    const normalizedLaunchpad = Array.from(new Set(rawLaunchpad.map((d: string) => d.slice(0, 10)))).sort();
                    setLaunchpadDates(normalizedLaunchpad);

                    if (sumRes.data) {
                        const summaryData = sumRes.data as any;
                        const byType: Record<SessionType, number> = { LAUNCHPAD: 0, BASECAMP: 0, DOMAIN: 0, OJT: 0 };
                        SESSION_OPTIONS.forEach((type) => {
                            byType[type] = summaryData.recorded_sessions_by_type?.[type] ?? 0;
                        });
                        setSummary({
                            recorded_sessions_total: summaryData.recorded_sessions_total ?? 0,
                            recorded_sessions_by_type: byType,
                            batch_avg_pct_conducted: summaryData.batch_avg_pct_conducted ?? 0,
                            trainee_avg_pct_conducted: summaryData.trainee_avg_pct_conducted ?? 0,
                            session_hours_by_date: normalizeHoursMap(summaryData.session_hours_by_date),
                            daily: (summaryData.daily ?? []).map((d: any) => ({
                                date: d.date,
                                pct: d.pct,
                                hours: toHourValue(d.hours),
                            })),
                            launchpad_sessions_total: summaryData.launchpad_sessions_total ?? 0,
                            launchpad_daily: (summaryData.launchpad_daily ?? []).map((d: any) => ({
                                date: d.date,
                                pct: d.pct,
                                present: d.present,
                                roster: d.roster,
                                hours: toHourValue(d.hours),
                            })),
                        });
                    } else {
                        setSummary(null);
                    }
                    const dt: Record<string, string> = sessRes.data?.date_to_type ?? {};
                    setDateToType(dt);
                    // build trainee map
                    const traineesMap = new Map<number, TraineeWithAttendance>();
                    batch.fullBatchData.batch_trainees.forEach(bt => {
                        traineesMap.set(bt.trainee_id, {
                            id: bt.trainee_id,
                            name: bt.trainee.user.full_name,
                            attendance: {}, // filled below
                        });
                    });

                    const records: AttendanceRecordApiResponse[] = recRes?.data || [];
                    records.forEach(r => {
                        const tr = traineesMap.get(r.trainee_id);
                        if (!tr && r.trainee?.user?.full_name) {
                            traineesMap.set(r.trainee_id, {
                                id: r.trainee_id,
                                name: r.trainee.user.full_name,
                                attendance: {},
                            });
                        }
                    });

                    records.forEach(r => {
                        const tr = traineesMap.get(r.trainee_id);
                        if (!tr) return;
                        const d = r.session_date.slice(0, 10);
                        tr.attendance[d] = {
                            status: r.present ? 'P' : 'A',
                            remarks: r.remarks ?? undefined,
                            type: (r.session_type as SessionType | null) ?? '',
                        };
                    });

                    setTraineeData(Array.from(traineesMap.values()));
                }
                else {
                    setLaunchpadDates([]);
                    setError('Failed to fetch attendance data.');
                }
            } catch (e) {
                console.error(e);
                setError('Failed to fetch attendance data.');
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, [batch]);


    const handleRemarkClick = (trainee: TraineeWithAttendance, date: string) => {
        const dailyAttendance = trainee.attendance[date];
        if (dailyAttendance && dailyAttendance.remarks) {
            setSelectedTraineeForRemarks(trainee);
            setSelectedDateForRemarks(date);
        }
    };

    const handleCloseRemarksModal = () => {
        setSelectedTraineeForRemarks(null);
        setSelectedDateForRemarks(null);
    };
    const highlightLow = batch.attendancePercentage < 75;
    return (
        <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6 lg:p-8">
            <div className="flex items-center mb-6">
                <button onClick={onBack} className="flex items-center text-gray-500 hover:text-gray-900 transition-colors duration-200">
                    <ArrowLeft size={16} className="mr-2" />
                    Back to Dashboard
                </button>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Attendance for {batch.batchId}</h2>
            <p className="text-sm text-gray-500 mb-6">
                Program: {batch.program} | Trainer: {batch.trainer}
                <span
                    className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
                        TRAINER_TAG_BADGE_STYLES[batch.trainerTag as keyof typeof TRAINER_TAG_BADGE_STYLES] ??
                        "bg-slate-100 text-slate-700"
                    }`}
                >
                    {batch.trainerTag}
                </span>
            </p>


            {/* Summary chips */}
            {summary && (
                <div className="mb-4 flex flex-wrap gap-3 text-sm">
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-violet-50 text-blue-700">
                        Recorded Sessions: <b className="ml-1">{summary?.recorded_sessions_total ?? 0}</b>
                    </span>
                    {SESSION_OPTIONS.map((type) => (
                        <span key={type} className="inline-flex items-center px-3 py-1 rounded-full bg-slate-50 text-slate-700">
                            {typeLabel(type)}: <b className="ml-1">{summary?.recorded_sessions_by_type?.[type] ?? 0}</b>
                        </span>
                    ))}
                    {launchpadDatesSet.size > 0 && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700">
                            Launchpad Sessions Recorded: <b className="ml-1">{summary?.launchpad_sessions_total ?? launchpadDatesSet.size}</b>
                        </span>
                    )}
                    <span className={`inline-flex items-center px-3 py-1 rounded-full ${highlightLow ? 'bg-red-50  text-red-700' : 'bg-pearl-50  text-emerald-700 '}`}>
                        Batch Avg (recorded days): <b className="ml-1">{summary?.batch_avg_pct_conducted ?? 0}%</b>
                    </span>
                    {averageHours != null && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-indigo-50 text-indigo-700">
                            Avg Hours (recorded days): <b className="ml-1">{averageHours} hrs</b>
                        </span>
                    )}
                    {launchpadAveragePct != null && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-sky-50 text-sky-700">
                            Launchpad Avg Attendance: <b className="ml-1">{launchpadAveragePct}%</b>
                        </span>
                    )}
                </div>
            )}

            {/* Launchpad summary block */}
            {summary?.launchpad_daily?.length ? (
                <div className="mb-6 rounded-lg border border-blue-100 bg-blue-50 p-4">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <h3 className="text-base font-semibold text-blue-900">Launchpad attendance summary</h3>
                            <p className="text-xs text-blue-700">Launchpad sessions are tracked separately from core attendance.</p>
                        </div>
                        <div className="text-sm text-blue-800">
                            Sessions recorded: <b>{summary.launchpad_sessions_total}</b>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-blue-200 text-sm">
                            <thead className="bg-blue-100 text-blue-900">
                                <tr>
                                    <th className="px-3 py-2 text-left font-medium">Date</th>
                                    <th className="px-3 py-2 text-right font-medium">Present</th>
                                    <th className="px-3 py-2 text-right font-medium">Absent</th>
                                    <th className="px-3 py-2 text-right font-medium">Unmarked</th>
                                    <th className="px-3 py-2 text-right font-medium">Roster</th>
                                    <th className="px-3 py-2 text-right font-medium">% Present</th>
                                    <th className="px-3 py-2 text-right font-medium">Hours</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-blue-100">
                                {summary.launchpad_daily.map((row) => {
                                    const absent = Math.max(0, (row.roster ?? 0) - row.present);
                                    const unmarked = Math.max(0, (row.roster ?? 0) - row.present - absent);
                                    return (
                                        <tr key={row.date}>
                                            <td className="px-3 py-2 text-blue-900">{prettyMD(row.date)}</td>
                                            <td className="px-3 py-2 text-right text-blue-900">{row.present}</td>
                                            <td className="px-3 py-2 text-right text-blue-900">{absent}</td>
                                            <td className="px-3 py-2 text-right text-blue-900">{unmarked}</td>
                                            <td className="px-3 py-2 text-right text-blue-900">{row.roster ?? '—'}</td>
                                            <td className="px-3 py-2 text-right text-blue-900">{row.pct}%</td>
                                            <td className="px-3 py-2 text-right text-blue-900">{row.hours != null ? row.hours : '—'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : null}

            <div className="mb-4 flex items-center gap-2">
                <Search size={16} className="text-gray-400" />
                <input
                    type="text"
                    value={searchTrainee}
                    onChange={(e) => setSearchTrainee(e.target.value)}
                    placeholder="Search trainee by name"
                    className="block w-full max-w-xs rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
                {searchTrainee && (
                    <button
                        onClick={() => setSearchTrainee('')}
                        className="text-xs text-gray-500 hover:text-gray-700"
                        title="Clear"
                    >
                        Clear
                    </button>
                )}
            </div>


            {isLoading ? (
                <div className="flex justify-center items-center h-40">
                    <Loader2 size={32} className="animate-spin text-gray-400" />
                </div>
            ) : error ? (
                <div className="text-center text-red-500 p-4 bg-red-50 rounded-lg">
                    <p>{error}</p>
                </div>
            ) : traineeData.length > 0 && allDates.length > 0 ? (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="sticky left-0 bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider z-10">
                                    Trainee Name
                                </th>
                                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Coverage
                                </th>
                                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Launchpad
                                </th>

                                {allDates.map(date => {
                                    const t = dateToType[date] as SessionType | '' | undefined;
                                    const recordedHours = (summary?.session_hours_by_date ?? {})[date] ?? hoursByDate[date] ?? null;
                                    return (
                                        <th key={date} className="px-3 py-2 text-center text-xs font-medium text-gray-700 min-w-[90px]">
                                            <div className="font-semibold">{prettyMD(date)}</div>
                                            <span className={`mt-1 inline-flex px-2 py-0.5 rounded-full text-[10px] ${typeBadge(t)}`}>
                                                {typeLabel(t)}
                                            </span>
                                            {recordedHours != null && (
                                                <div className="mt-1 text-[10px] text-gray-500">{recordedHours} hrs</div>
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredTrainees.map((trainee) => {
                                const s = statsById.get(trainee.id); // ✅ compute here
                                const lp = launchpadStatsById.get(trainee.id);

                                return (
                                    <tr key={trainee.id}>
                                        <td className="sticky left-0 bg-white px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 z-10">
                                            {trainee.name}
                                        </td>
                                        {/* Coverage cell */}
                                        <td className="px-3 py-4 whitespace-nowrap text-sm text-center">
                                            {s?.denom ? (
                                                <div title={`${s.present}/${s.denom} conducted days • A:${s.absent} • U:${s.unmarked}`}>
                                                    <div className="w-24 bg-gray-200 rounded-full h-2 mx-auto">
                                                        <div
                                                            className={`${s.pct < 65 ? 'bg-red-500' : 'bg-blue-600'} h-2 rounded-full`}
                                                            style={{ width: `${s.pct}%` }}
                                                        />
                                                    </div>
                                                    <div className="mt-1 text-xs text-gray-700">
                                                        {s.pct}% <span className="text-gray-400">({s.present}/{s.denom})</span>
                                                    </div>
                                                </div>
                                            ) : '—'}
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap text-sm text-center">
                                            {launchpadDatesSet.size > 0 ? (
                                                <div className="text-xs text-gray-700">
                                                    <span className="font-semibold">{lp?.present ?? 0}</span>
                                                    <span className="text-gray-400"> / {launchpadDatesSet.size}</span>
                                                    <div className="mt-0.5 text-[11px] text-gray-500">{lp?.pct ?? 0}%</div>
                                                </div>
                                            ) : '—'}
                                        </td>
                                        {allDates.map(date => {
                                            const dailyAttendance = trainee.attendance[date];
                                            const status = dailyAttendance ? (dailyAttendance.status) : 'N/A';
                                            const statusColor = status === 'P' ? 'text-green-600' : status === 'A' ? 'text-red-600' : 'text-gray-400';
                                            const statusBg = status === 'P' ? 'bg-green-100' : status === 'A' ? 'bg-red-100' : 'bg-gray-100';

                                            return (
                                                <td key={date} className="px-3 py-4 whitespace-nowrap text-sm text-center">
                                                    <div
                                                        className={`inline-flex items-center justify-center h-8 w-8 rounded-full font-bold ${statusBg} ${statusColor} cursor-pointer`}
                                                        onClick={() => handleRemarkClick(trainee, date)}
                                                    >
                                                        {status}
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                    <p className="mt-2 text-xs text-gray-500">
                        Note: “Conducted sessions” exclude Launchpad days. Launchpad attendance is shown separately for reference and does not affect core attendance metrics.
                    </p>

                </div>
            ) : (
                <div className="text-center text-gray-500 p-8">
                    <p>No attendance data found for this batch.</p>
                </div>
            )}
            {selectedTraineeForRemarks && selectedDateForRemarks && (
                <RemarksModal
                    trainee={selectedTraineeForRemarks}
                    date={selectedDateForRemarks}
                    onClose={handleCloseRemarksModal}
                />
            )}
        </div>
    );
};

// Component for marking/editing attendance for a batch
const AttendanceMarker = ({ batch, onBack }: { batch: Batch; onBack: () => void }) => {
    const [trainees, setTrainees] = useState<Trainee[]>([]);
    const [attendance, setAttendance] = useState<Record<number, { status: 'P' | 'A' | null }>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');

    const [sessionDate, setSessionDate] = useState<string>('');
    const [sessionType, setSessionType] = useState<SessionType | ''>('');
    const [sessionHours, setSessionHours] = useState<string>('');
    const [allowAnyDate, setAllowAnyDate] = useState(false);
    const [availableSessionTypes, setAvailableSessionTypes] = useState<SessionType[]>(SESSION_OPTIONS);
    const [allSessions, setAllSessions] = useState<string[]>([]);
    const [recordedDates, setRecordedDates] = useState<Set<string>>(new Set());
    const [selectableSessions, setSelectableSessions] = useState<string[]>([]);
    const [dateToType, setDateToType] = useState<Record<string, string>>({});
    const [hoursByDate, setHoursByDate] = useState<Record<string, number | null>>({});
    const [searchTrainee, setSearchTrainee] = useState('');

    const filteredTrainees = useMemo(() => {
        const s = searchTrainee.trim().toLowerCase();
        if (!s) return trainees;
        return trainees.filter(t => t.name.toLowerCase().includes(s));
    }, [trainees, searchTrainee]);


    const renderDayContents = (day: number, date: Date) => {
        const key = fmtLocal(date);
        const t = dateToType[key] as SessionType | '' | undefined;
        const marked = recordedDates.has(key);
        return (
            <div className="relative">
                <span className={marked ? 'font-semibold' : ''}>{day}</span>
                {t && <span className={`absolute -bottom-0.5 left-1/2 -translate-x-1/2 h-1.5 w-1.5 rounded-full ${dotClass(t)}`} />}
                {marked && (
                    <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-green-500" title="Attendance marked" />
                )}
            </div>
        );
    };

    useEffect(() => {
        const load = async () => {
            try {
                const res = await attendanceApi.getBatchSessions(String(batch.fullBatchData.batch_id));
                const payload = res.data ?? {};

                const toHourValue = (value: any): number | null => {
                    if (value === null || value === undefined || value === '') return null;
                    const num = Number(value);
                    return Number.isFinite(num) ? Math.round(num * 100) / 100 : null;
                };

                const normalizeHoursMap = (input: Record<string, any> | undefined): Record<string, number | null> => {
                    const result: Record<string, number | null> = {};
                    if (input && typeof input === 'object') {
                        Object.entries(input).forEach(([key, value]) => {
                            result[key] = toHourValue(value);
                        });
                    }
                    return result;
                };

                const sessionsAll: string[] = payload.sessions_all ?? [];
                setAllSessions(sessionsAll);
                // Dates that already have any attendance recorded (launchpad + non-launchpad).
                const recorded = new Set<string>([
                    ...(payload.recorded_sessions ?? []),
                    ...(payload.launchpad_sessions ?? []),
                    ...(payload.non_launchpad_sessions ?? []),
                    ...sessionsAll,
                ]);
                setRecordedDates(recorded);
                setSelectableSessions(payload.selectable_sessions ?? []);
                setDateToType(payload.date_to_type ?? {});
                const normalizedHours = normalizeHoursMap(payload.session_hours_by_date);
                setHoursByDate(normalizedHours);
                setAllowAnyDate(Boolean(payload.allow_any_date));
                if (Array.isArray(payload.session_types) && payload.session_types.length) {
                    setAvailableSessionTypes(payload.session_types as SessionType[]);
                } else {
                    setAvailableSessionTypes(SESSION_OPTIONS);
                }

                const today = fmtLocal(new Date());
                let initialDate = '';
                if (payload.allow_any_date) {
                    initialDate = today;
                } else {
                    const selectable = payload.selectable_sessions ?? [];
                    if (selectable.includes(today)) {
                        initialDate = today;
                    } else if (selectable.length) {
                        initialDate = selectable[selectable.length - 1];
                    }
                }
                if (!initialDate && sessionsAll.length) {
                    initialDate = sessionsAll[sessionsAll.length - 1];
                }

                setSessionDate(initialDate);
                const inferredType = payload.date_to_type?.[initialDate] as SessionType | undefined;
                setSessionType(inferredType ?? '');
                setSessionHours(initialDate ? (() => {
                    const val = normalizedHours[initialDate];
                    return val != null ? String(val) : '';
                })() : '');
            } catch (err) {
                console.error(err);
            }
        };
        load();
    }, [batch]);

    const resetAttendanceList = useCallback((list: Trainee[]) => {
        setTrainees(list);
        const initial: Record<number, { status: 'P' | 'A' | null }> = {};
        list.forEach(t => {
            initial[t.id] = { status: null };
        });
        setAttendance(initial);
    }, []);

    const loadTraineesForSession = useCallback(async () => {
        // Launchpad uses the registered trainees list; all other sessions use enrolled batch trainees
        if (sessionType === 'LAUNCHPAD') {
            const mobilisationId = batch.fullBatchData.mobilisation_id;
            if (!mobilisationId) {
                setError('Mobilisation not linked to this batch. Cannot load registered trainees.');
                resetAttendanceList([]);
                return;
            }
            try {
                const res = await traineeRegistrationApi.getTrainees({
                    mobilisation_id: mobilisationId,
                    limit: 2000,
                });
                if (res.success && Array.isArray(res.data)) {
                    const list = res.data.map(reg => ({
                        id: reg.trainee.trainee_id,
                        name: reg.trainee.user.full_name,
                    }));
                    setError(null);
                    resetAttendanceList(list);
                } else {
                    setError(res.message || 'Unable to load registered trainees for launchpad.');
                    resetAttendanceList([]);
                }
            } catch (err) {
                console.error(err);
                setError('Failed to load registered trainees for launchpad.');
                resetAttendanceList([]);
            }
        } else {
            const rows = batch.fullBatchData.batch_trainees || [];
            const list = rows.map(bt => ({
                id: bt.trainee_id,
                name: bt.trainee.user.full_name,
            }));
            setError(null);
            resetAttendanceList(list);
        }
    }, [batch.fullBatchData.batch_trainees, batch.fullBatchData.mobilisation_id, resetAttendanceList, sessionType, setError]);

    useEffect(() => {
        loadTraineesForSession();
    }, [loadTraineesForSession]);

    useEffect(() => {
        if (!sessionDate) {
            setSessionType('');
            setSessionHours('');
            return;
        }
        const inferred = dateToType[sessionDate] as SessionType | undefined;
        if (inferred) {
            setSessionType(inferred);
        }
        const existingHours = hoursByDate[sessionDate];
        setSessionHours(existingHours != null ? String(existingHours) : '');
    }, [sessionDate, dateToType, hoursByDate]);

    const handleStatusChange = (id: number, status: 'P' | 'A') => {
        setAttendance(prev => ({ ...prev, [id]: { ...(prev[id] ?? { status: null }), status } }));
    };

    const isComplete = useMemo(
        () => trainees.length > 0 && trainees.every(t => attendance[t.id]?.status !== null),
        [trainees, attendance]
    );

    const handleSave = async () => {
        setIsSaving(true);
        setError(null);

        if (!sessionDate) {
            setError('Please select a session date.');
            setIsSaving(false);
            return;
        }
        if (!allowAnyDate && !selectableSessions.includes(sessionDate)) {
            setError('Selected date is not a valid session for this batch.');
            setIsSaving(false);
            return;
        }
        if (!isComplete) {
            setError('Mark all trainees before saving.');
            setIsSaving(false);
            return;
        }
        if (!sessionType) {
            setError('Please select a session type.');
            setIsSaving(false);
            return;
        }
        const trimmedHours = sessionHours.trim();
        if (!trimmedHours) {
            setError('Please enter the total training hours conducted.');
            setIsSaving(false);
            return;
        }
        const parsedHours = Number(trimmedHours);
        if (!Number.isFinite(parsedHours)) {
            setError('Training hours must be a valid number.');
            setIsSaving(false);
            return;
        }
        if (parsedHours <= 0 || parsedHours > 24) {
            setError('Training hours must be greater than 0 and at most 24.');
            setIsSaving(false);
            return;
        }
        const normalizedHours = Math.round(parsedHours * 100) / 100;

        try {
            const items = trainees.map(t => ({
                trainee_id: t.id,
                present: attendance[t.id]?.status === 'P',
            }));
            await attendanceApi.markAttendanceBulk(
                String(batch.fullBatchData.batch_id),
                {
                    session_date: sessionDate,
                    session_type: sessionType as SessionType,
                    session_hours: normalizedHours,
                    items,
                }
            );
            setHoursByDate(prev => ({ ...prev, [sessionDate]: normalizedHours }));
            setSessionHours(String(normalizedHours));
            setAlertMessage('Attendance saved successfully!');
            setShowAlert(true);
        } catch (e) {
            console.error(e);
            setError('Failed to save attendance.');
        } finally {
            setIsSaving(false);
        }
    };


    const closeAlert = () => {
        setShowAlert(false);
        onBack();
    };

    const restrictionMessage = allowAnyDate
        ? 'You can mark attendance for any date.'
        : 'You can mark attendance for today (if it is a session) and the last session day only.';

    const dateOptions = allowAnyDate
        ? undefined
        : Array.from(new Set([...allSessions, ...selectableSessions]))
            .map(parseLocal)
            .filter(Boolean) as Date[];

    return (
        <div className="bg-white rounded-2xl shadow-md p-6">
            {showAlert && (
                <ModalPortal>
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white p-6 rounded-lg">
                        <p className="mb-4">{alertMessage}</p>
                        <button onClick={closeAlert} className="px-4 py-2 bg-blue-600 text-white rounded">
                            OK
                        </button>
                        </div>
                    </div>
                </ModalPortal>
            )}

            <button onClick={onBack} className="text-gray-500 hover:text-gray-900 mb-4 flex items-center">
                <ArrowLeft size={16} className="mr-2" /> Back to Dashboard
            </button>

            <h2 className="text-2xl font-bold mb-2">Mark Attendance for {batch.batchId}</h2>
            <p className="text-gray-500 mb-4">
                Program: {batch.program} | Trainer: {batch.trainer}
                <span
                    className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
                        TRAINER_TAG_BADGE_STYLES[batch.trainerTag as keyof typeof TRAINER_TAG_BADGE_STYLES] ??
                        "bg-slate-100 text-slate-700"
                    }`}
                >
                    {batch.trainerTag}
                </span>
            </p>

            <div className="mb-4 relative z-0">
                <label className="block text-sm font-medium text-gray-700 mb-1">Session Date</label>
                <ReactDatePicker
                    selected={parseLocal(sessionDate)}
                    onChange={(d) => setSessionDate(d ? fmtLocal(d) : '')}
                    includeDates={dateOptions}
                    filterDate={allowAnyDate ? undefined : (d) => selectableSessions.includes(fmtLocal(d))}
                    dayClassName={(date) => {
                        const t = dateToType[fmtLocal(date)] as SessionType | '' | undefined;
                        return t ? `has-session ${typeBadge(t)}` : '';
                    }}
                    renderDayContents={renderDayContents}
                    placeholderText="Pick a session date"
                    dateFormat="yyyy-MM-dd"
                    popperPlacement="bottom-start"
                    popperClassName="z-[99999]"
                    portalId="datepicker-portal"
                    showPopperArrow={false}
                    className="mt-1 block w-56 rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
                {sessionDate && (
                    <div className="mt-2 text-sm text-gray-700">
                        Selected: <span className="font-medium">{prettyMD(sessionDate)}</span>{' '}
                        {sessionType ? (
                            <span className={`ml-2 inline-flex px-2 py-0.5 rounded-full text-xs ${typeBadge(sessionType)}`}>
                                {typeLabel(sessionType)}
                            </span>
                        ) : (
                            <span className="ml-2 text-xs text-gray-500">Select type</span>
                        )}
                        {sessionHours.trim() && (
                            <span className="ml-2 text-xs text-gray-600">{sessionHours} hrs</span>
                        )}
                        {recordedDates.has(sessionDate) ? (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Already marked
                            </span>
                        ) : (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                Not marked yet
                            </span>
                        )}
                    </div>
                )}

                <label className="block text-sm font-medium text-gray-700 mt-4 mb-1">Session Type</label>
                <select
                    value={sessionType}
                    onChange={(e) => setSessionType(e.target.value as SessionType | '')}
                    className="mt-1 block w-56 rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                    <option value="">Select session type</option>
                    {availableSessionTypes.map((t) => (
                        <option key={t} value={t}>
                            {typeLabel(t)}
                        </option>
                    ))}
                </select>
                <label className="block text-sm font-medium text-gray-700 mt-4 mb-1">Total Training Hours</label>
                <input
                    type="number"
                    min="0"
                    max="24"
                    step="0.25"
                    value={sessionHours}
                    onChange={(e) => setSessionHours(e.target.value)}
                    placeholder="e.g. 6"
                    className="mt-1 block w-56 rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">Enter the total hours conducted across sessions for this date.</p>

                <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                    {SESSION_OPTIONS.map(t => (
                        <span key={t} className={`inline-flex items-center px-2 py-0.5 rounded-full ${typeBadge(t)}`}>
                            {typeLabel(t)}
                        </span>
                    ))}
                </div>

                <p className="mt-2 text-xs text-gray-500">{restrictionMessage}</p>
            </div>

            <div className="mb-3 flex items-center gap-2">
                <Search size={16} className="text-gray-400" />
                <input
                    type="text"
                    value={searchTrainee}
                    onChange={(e) => setSearchTrainee(e.target.value)}
                    placeholder="Search trainee by name"
                    className="block w-full max-w-xs rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
                {searchTrainee && (
                    <button
                        onClick={() => setSearchTrainee('')}
                        className="text-xs text-gray-500 hover:text-gray-700"
                        title="Clear"
                    >
                        Clear
                    </button>
                )}
            </div>

            {error && (
                <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-600">
                    {error}
                </div>
            )}

            <div className="overflow-auto max-h-[60vh] border rounded">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                        <tr>
                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Name</th>
                            <th className="px-4 py-2 text-center text-sm font-medium text-gray-700">Present</th>
                            <th className="px-4 py-2 text-center text-sm font-medium text-gray-700">Absent</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredTrainees.map(t => (
                            <tr key={t.id}>
                                <td className="px-4 py-3 text-sm text-gray-800">{t.name}</td>
                                <td className="px-4 py-3 text-center">
                                    <input
                                        type="radio"
                                        name={`status-${t.id}`}
                                        checked={attendance[t.id]?.status === 'P'}
                                        onChange={() => handleStatusChange(t.id, 'P')}
                                    />
                                </td>
                                <td className="px-4 py-3 text-center">
                                    <input
                                        type="radio"
                                        name={`status-${t.id}`}
                                        checked={attendance[t.id]?.status === 'A'}
                                        onChange={() => handleStatusChange(t.id, 'A')}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <button
                onClick={handleSave}
                disabled={!sessionDate || !isComplete || isSaving || !sessionType || !sessionHours.trim()}
                className="mt-4 w-full py-2 px-4 bg-blue-600 text-white rounded disabled:opacity-50"
            >
                {isSaving ? <Loader2 className="animate-spin" /> : 'Save Attendance'}
            </button>
        </div>
    );
};



// Main App component
const App = () => {
    const { user } = useAuth()
    const [batches, setBatches] = useState<Batch[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentView, setCurrentView] = useState<'dashboard' | 'details' | 'marker'>('dashboard');
    const [selectedBatch, setSelectedBatch] = useState<Batch | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [traineeLoading, setTraineeLoading] = useState(false);
    const [traineeError, setTraineeError] = useState<string | null>(null);
    const [traineeAttendance, setTraineeAttendance] = useState<Array<{
        batchCode: string;
        attendancePercentage: number;
        sessions: TraineeSession[];
    }>>([]);


    useEffect(() => {
        const fetchBatches = async () => {
            setIsLoading(true);
            try {
                const response = await attendanceApi.getAllBatchesAttendance();
                if (response.data) {
                    // Map the API response to the local Batch type for easier use in components
                    // The corrected line with a type assertion
                    const mappedBatches: Batch[] = (response.data as BatchApiResponse[]).map((b) => {
                        const trainerTag = (b as any).trainerTag ?? 'Trainer';
                        const trainerName =
                            (b as any).trainerName ?? b.trainer?.user?.full_name ?? 'N/A';
                        const launchpadSessionsRecorded = (b as any).launchpadSessionsRecorded ?? 0;
                        const launchpadAvgPct = (b as any).launchpadAvgPct ?? null;
                        return {
                            batchId: b.batch_code,
                            program: b.program?.name || 'N/A',
                            trainer: trainerName,
                            trainerTag,
                            launchpadSessionsRecorded,
                            launchpadAvgPct,
                            trainees: b.batch_trainees?.length || 0,
                            attendancePercentage: b.attendancePercentage || 0,
                            status: b.status ?? 'N/A',
                            batchProposalStatus: (b as any).batch_proposal_status ?? 'N/A',
                            fullBatchData: b, // Store the full API response for nested components
                        };
                    });

                    setBatches(mappedBatches);
                }
            } catch (err) {
                console.log(err);
                setError('Failed to fetch batches.');
            } finally {
                setIsLoading(false);
            }
        };
        fetchBatches();
    }, []);

    useEffect(() => {
        const loadTraineeAttendance = async () => {
            if (user?.role !== 'TRAINEE' || !user.user_id) return;
            setTraineeLoading(true);
            setTraineeError(null);
            try {
                const res = await attendanceApi.getAllBatchesAttendance();
                const all = (res.data as BatchApiResponse[]) || [];
                const mine = all.filter(b =>
                    b.batch_trainees?.some(t => t.trainee?.user?.user_id === user.user_id)
                );
                const detailed = await Promise.all(mine.map(async (b) => {
                    const traineeEntry = b.batch_trainees?.find(t => t.trainee?.user?.user_id === user.user_id);
                    const traineeId = traineeEntry?.trainee_id;
                    let sessions: TraineeSession[] = [];
                    if (traineeId) {
                        try {
                            const rec = await attendanceApi.getBatchAttendance(String(b.batch_id));
                            sessions = (rec.data || [])
                                .filter(r => r.trainee_id === traineeId)
                                .map(r => ({
                                    date: r.session_date,
                                    present: r.present,
                                    type: r.session_type ?? null,
                                    hours: r.session_hours ?? null,
                                    remarks: r.remarks ?? null,
                                }));
                        } catch (e) {
                            console.error('Failed to fetch batch attendance for trainee', e);
                        }
                    }
                    return {
                        batchCode: b.batch_code,
                        attendancePercentage: b.attendancePercentage || 0,
                        sessions,
                    };
                }));
                setTraineeAttendance(detailed);
            } catch (err: any) {
                console.error(err);
                setTraineeError('Failed to load your attendance. Please try again.');
            } finally {
                setTraineeLoading(false);
            }
        };

        loadTraineeAttendance();
    }, [user]);

    const exportData = async (batch: Batch) => {
        try {
            setError(null);
            const res = await attendanceApi.exportBatchCsv(String(batch.fullBatchData.batch_id));
            const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `attendance_batch_${batch.batchId}.csv`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (e: any) {
            console.error('Export failed', e);
            setError(`Failed to export: ${e}`)
        }
    }

    const handleViewDetails = (batch: Batch) => {
        setSelectedBatch(batch);
        setCurrentView('details');
    };

    const handleMarkAttendance = (batch: Batch) => {
        setSelectedBatch(batch);
        setCurrentView('marker');
    };

    const handleEditAttendance = (batch: Batch) => {
        setSelectedBatch(batch);
        setCurrentView('marker');
    };

    const handleBackToDashboard = () => {
        setSelectedBatch(null);
        setCurrentView('dashboard');
    };

    const filteredBatches = useMemo(() => {
        const term = searchTerm.toLowerCase();
        return batches.filter(batch =>
            batch.batchId.toLowerCase().includes(term) ||
            batch.program.toLowerCase().includes(term) ||
            batch.trainer.toLowerCase().includes(term) ||
            batch.trainerTag.toLowerCase().includes(term)
        );
    }, [batches, searchTerm]);

    if (user?.role === 'TRAINEE') {
        return (
            <div className="font-sans antialiased text-gray-900 bg-gray-100 min-h-screen p-4 sm:p-8">
                <div className="max-w-5xl mx-auto">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">Your Attendance</h1>
                    {traineeLoading ? (
                        <div className="flex items-center justify-center py-10">
                            <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
                        </div>
                    ) : traineeError ? (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                            {traineeError}
                        </div>
                    ) : traineeAttendance.length === 0 ? (
                        <div className="text-sm text-gray-600">No attendance records found for your account.</div>
                    ) : (
                        <div className="space-y-6">
                            {traineeAttendance.map((item) => (
                                <div key={item.batchCode} className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm">
                                    <div className="flex items-center justify-between mb-3">
                                        <div>
                                            <p className="text-sm text-gray-500">Batch</p>
                                            <p className="text-lg font-semibold text-gray-900">{item.batchCode}</p>
                                        </div>
                                        <div className="text-sm text-gray-700">
                                            Attendance: <span className="font-semibold">{Math.round(item.attendancePercentage)}%</span>
                                        </div>
                                    </div>
                                    {item.sessions.length === 0 ? (
                                        <p className="text-sm text-gray-500">No session records yet.</p>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hours</th>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {item.sessions.map((s) => (
                                                        <tr key={`${item.batchCode}-${s.date}`}>
                                                            <td className="px-4 py-2 text-gray-700">{new Date(s.date).toLocaleDateString()}</td>
                                                            <td className="px-4 py-2">
                                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${s.present ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                                    {s.present ? 'Present' : 'Absent'}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-2 text-gray-700">{typeLabel(s.type ?? '')}</td>
                                                            <td className="px-4 py-2 text-gray-700">{s.hours ?? '—'}</td>
                                                            <td className="px-4 py-2 text-gray-500">{s.remarks ?? '—'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="font-sans antialiased text-gray-900 bg-gray-100 min-h-screen p-4 sm:p-8">
            <div className="max-w-7xl mx-auto">
                <div className="flex justify-between items-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Attendance Management</h1>
                    {/* <button
                        className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-colors duration-200"
                    >
                        <FileDown size={16} className="mr-2" />
                        Export Data
                    </button> */}
                </div>
                {currentView === 'dashboard' && (
                    <>
                        <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6 lg:p-8 mb-6">
                            <div className="flex items-center space-x-4">
                                <Search size={20} className="text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search by Batch ID, Program, Trainer or Tag"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="block w-full border-0 focus:ring-0 text-lg placeholder-gray-400"
                                />
                            </div>
                        </div>

                        {isLoading ? (
                            <div className="flex justify-center items-center h-64">
                                <Loader2 size={48} className="animate-spin text-gray-400" />
                            </div>
                        ) : error ? (
                            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg relative" role="alert">
                                <p>{error}</p>
                            </div>
                        ) : filteredBatches.length > 0 ? (
                            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Batch Details
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Batch Status
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Batch Proposal Status
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Trainer
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Trainees
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Attendance
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Launchpad
                                                </th>
                                                <th scope="col" className="relative px-6 py-3">
                                                    <span className="sr-only">Actions</span>
                                                </th>
                                                <th>

                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {filteredBatches.map((batch) => {
                                                const highlightLow = batch.attendancePercentage < 75;
                                                return (
                                                    <tr
                                                        key={batch.batchId}
                                                        className={highlightLow ? 'bg-red-50' : ''}
                                                    >
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm font-medium text-gray-900">{batch.batchId}</div>
                                                            <div className="text-sm text-gray-500">{batch.program}</div>
                                                            {/* <div className="text-xs text-gray-400">{batch.startDate} to {batch.endDate}</div> */}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex flex-col space-y-1"><span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full`}>{batch.status}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex flex-col space-y-1">
                                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full`}>{batch.fullBatchData.batch_proposal_status}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            <div className="flex items-center gap-2">
                                                                <span>{batch.trainer}</span>
                                                                <span
                                                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${TRAINER_TAG_BADGE_STYLES[batch.trainerTag as keyof typeof TRAINER_TAG_BADGE_STYLES] ?? "bg-slate-100 text-slate-700"}`}
                                                                >
                                                                    {batch.trainerTag}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {batch.trainees}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            <div className="flex items-center">
                                                                <div className="w-20 bg-gray-200 rounded-full h-2">
                                                                    <div className={`${highlightLow ? 'bg-red-500' : 'bg-blue-600'} h-2 rounded-full`} style={{ width: `${batch.attendancePercentage}%` }}></div>
                                                                </div>
                                                                <span className={`${highlightLow ? 'text-red-600' : 'text-gray-600'}  ml-2 text-xs font-medium text-gray-600`}>{Math.round(batch.attendancePercentage)}%</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {batch.launchpadSessionsRecorded ? (
                                                                <div className="flex flex-col">
                                                                    <span className="text-gray-800 text-sm font-semibold">{batch.launchpadSessionsRecorded} sessions</span>
                                                                    <span className="text-xs text-gray-500">
                                                                        Avg: {batch.launchpadAvgPct != null ? `${batch.launchpadAvgPct}%` : '—'}
                                                                    </span>
                                                                </div>
                                                            ) : (
                                                                <span className="text-gray-400">—</span>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                            <div className="flex items-center space-x-3">
                                                                <button onClick={() => handleViewDetails(batch)}
                                                                    className="text-blue-600 hover:text-blue-900"
                                                                >
                                                                    View Details
                                                                </button>
                                                                <span className="text-gray-300">|</span>
                                                                {user?.role != 'TRAINEE' && (<div>
                                                                    <button
                                                                        onClick={() => handleMarkAttendance(batch)}
                                                                        className={`text-blue-600 hover:text-blue-900}`}
                                                                    >
                                                                        Mark Attendance
                                                                    </button>
                                                                    <span className="text-gray-300">|</span>
                                                                    <button
                                                                        onClick={() => exportData(batch)}
                                                                        className={`text-blue-600 hover:text-blue-900}`}
                                                                    >
                                                                        Export Data
                                                                    </button>


                                                                </div>)}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white rounded-2xl shadow-sm text-center text-gray-500 p-8">
                                <p>No Inprogress or Completed batche found.</p>
                            </div>
                        )}
                    </>
                )}
                {currentView === 'details' && selectedBatch && (
                    <BatchDetails batch={selectedBatch} onBack={handleBackToDashboard} onEditAttendance={handleEditAttendance} />
                )}
                {currentView === 'marker' && selectedBatch && (
                    <AttendanceMarker batch={selectedBatch} onBack={handleBackToDashboard} />
                )}
            </div>
        </div>
    );
};

export default App;
